import { useQuery } from '@tanstack/react-query';
import { range } from 'lodash';

import { WaveFrequency, WaveSpectrumDatum } from '@/api/historicalData/types';
import { DirectionalWaveEnergyUnits, convert } from '@/common/convert';
import { QueryConfig } from '@/common/react-query';
import { usePreferredUnits } from '@/hooks/usePreferredUnits';
import { estimateDirectionalSpectrum } from '@/utils/estimateDirectionalSpectrum';

const DIRECTIONS = range(0, 360, 2);

export const getWaveEnergySpectrumData = async (
    payload: WaveFrequency,
    preferredUnit: DirectionalWaveEnergyUnits
): Promise<WaveSpectrumDatum[]> => {
    if (!payload) {
        return [];
    }
    const waveEnergy = estimateDirectionalSpectrum({ ...payload, directions: DIRECTIONS });

    const flattenedData = waveEnergy.spectrum.flatMap((energyRow, freq_i) =>
        energyRow.map((energy, dir_i) => ({
            freq: payload.frequency[freq_i],
            dir: DIRECTIONS[dir_i],
            energy: convert(energy).from('m^2/(Hz*Deg)').to(preferredUnit),
        }))
    );

    return flattenedData;
};

type UseEstimatedWaveEnergySpectrumOptions = {
    config?: QueryConfig<typeof getWaveEnergySpectrumData>;
    payload: WaveFrequency;
    spotterId: string;
    timestamp: string;
};

export const useEstimatedWaveEnergySpectrum = ({
    config = {},
    payload,
    spotterId,
    timestamp,
}: UseEstimatedWaveEnergySpectrumOptions) => {
    const preferredUnit = usePreferredUnits('directionalWaveEnergy');

    return useQuery<WaveSpectrumDatum[]>({
        queryKey: ['wave-energy-spectrum-data', { spotterId, timestamp, preferredUnit }],
        queryFn: () => getWaveEnergySpectrumData(payload, preferredUnit),
        staleTime: Infinity,
        ...config,
    });
};
